// DeclineCheckForm — "Record a decline check" (RRA v2.1 A9).
//
// Before an application that has an accommodation request open is declined, staff
// record that the ground has nothing to do with the request or its animal. The
// decline itself happens in AppFolio, so this is a record, not a gate. A decline
// that IS because of the animal is a decision on the request — that goes to an
// approver, and there is nothing to record here. The only free text is the
// ground (200 characters, neutral).

import { useState } from 'react';
import { RRA_DECLINE_GROUND_MAX } from '@/lib/rraCopy';
import type { RraDeclineCheckBody } from '@/lib/rraStaffClient';

export function DeclineCheckForm({
  busy,
  running,
  onSubmit,
  onCancel,
}: {
  /** True while any console action is running (disables the buttons). */
  busy: boolean;
  /** True while THIS form's action is the one running (the button's label). */
  running: boolean;
  onSubmit: (body: RraDeclineCheckBody) => void;
  onCancel: () => void;
}): React.JSX.Element {
  // No default: the question is asked, not assumed.
  const [becauseOfAnimal, setBecauseOfAnimal] = useState<boolean | null>(null);
  const [ground, setGround] = useState('');
  const [everyApplicant, setEveryApplicant] = useState(false);
  const [unrelated, setUnrelated] = useState(false);
  const ready = ground.trim().length > 0 && everyApplicant && unrelated;

  return (
    <div className="card" style={{ marginTop: 'var(--space-3)', background: 'var(--surface-2, transparent)' }}>
      <fieldset className="fieldset stack-sm">
        <legend>Is this decline because of the animal the request is about?</legend>
        <div className="cluster" style={{ gap: 'var(--space-5)' }}>
          <label className="cluster" style={{ gap: 'var(--space-2)', alignItems: 'center', cursor: 'pointer' }}>
            <input
              type="radio"
              name="decline-check-because"
              checked={becauseOfAnimal === true}
              onChange={() => setBecauseOfAnimal(true)}
            />
            <span>Yes</span>
          </label>
          <label className="cluster" style={{ gap: 'var(--space-2)', alignItems: 'center', cursor: 'pointer' }}>
            <input
              type="radio"
              name="decline-check-because"
              checked={becauseOfAnimal === false}
              onChange={() => setBecauseOfAnimal(false)}
            />
            <span>No</span>
          </label>
        </div>
      </fieldset>

      {becauseOfAnimal === true && (
        <div role="status" style={{ marginTop: 'var(--space-3)', fontSize: 'var(--text-sm)' }}>
          That's a decision on the request itself. Send it to an approver.
        </div>
      )}

      {becauseOfAnimal === false && (
        <div className="stack-sm" style={{ marginTop: 'var(--space-3)' }}>
          <div className="field">
            <label className="label" htmlFor="decline-check-ground">The ground</label>
            <input
              id="decline-check-ground"
              className="input"
              maxLength={RRA_DECLINE_GROUND_MAX}
              value={ground}
              onChange={(e) => setGround(e.target.value)}
            />
            <div style={{ color: 'var(--fg-muted)', fontSize: 'var(--text-xs)' }}>
              {ground.length}/{RRA_DECLINE_GROUND_MAX}
            </div>
          </div>
          <label className="cluster" style={{ gap: 'var(--space-2)', fontSize: 'var(--text-sm)', alignItems: 'center', cursor: 'pointer' }}>
            <input type="checkbox" checked={everyApplicant} onChange={(e) => setEveryApplicant(e.target.checked)} />
            <span>This ground applies to every applicant</span>
          </label>
          <label className="cluster" style={{ gap: 'var(--space-2)', fontSize: 'var(--text-sm)', alignItems: 'center', cursor: 'pointer' }}>
            <input type="checkbox" checked={unrelated} onChange={(e) => setUnrelated(e.target.checked)} />
            <span>It involves neither the request nor the animal</span>
          </label>
          <div>
            <button
              type="button"
              className="btn"
              data-variant="primary"
              data-size="sm"
              disabled={busy || !ready}
              onClick={() =>
                onSubmit({ ground: ground.trim(), appliesToEveryApplicant: true, unrelatedToRequestOrAnimal: true })
              }
            >
              {running ? 'Recording…' : 'Record the check'}
            </button>
          </div>
        </div>
      )}

      <div style={{ marginTop: 'var(--space-3)' }}>
        <button type="button" className="btn" data-variant="ghost" data-size="sm" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}
